// The sum tool (Σ, Marko 2026-11-03): one panel for cages, arrows and free sums, over the puzzle
// at the bottom or (when the clue is down there) the top. Total and Cells are dropdowns that take
// any value, then Repeats; the combinations as tiles below, scrolling inside the panel past five
// lines; then the digits. Green is in, red is out. Hide red (Marko, 2026-11-04) takes the
// combinations marked red off the list; it's remembered on the device.
import type { HelperMark } from '@zinc/shared';
import { useLayoutEffect, useState, type ReactNode, type RefObject } from 'react';
import { listText, tileLayout, type SumValues, type SumView } from '../../lib/helper';

const tileClass = (m: HelperMark | undefined) =>
  m === 1
    ? 'border-solved bg-solved-bg text-solved'
    : m === 2
      ? 'border-mistake bg-mistake-bg text-mistake'
      : 'border-accent-tint-2 bg-accent-tint text-accent-dark';

const digitClass = (m: HelperMark | undefined) =>
  m === 1
    ? 'border-solved bg-solved-bg font-bold text-solved'
    : m === 2
      ? 'border-mistake bg-mistake-bg text-mistake line-through decoration-2'
      : 'border-line bg-surface text-ink';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[11px] font-bold tracking-[0.05em] text-muted uppercase">{label}</span>
      {children}
    </div>
  );
}

function Dropdown(props: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <span className="relative inline-flex">
      <select
        aria-label={props.label}
        value={props.value}
        onChange={(e) => {
          props.onChange(e.target.value);
          // The next digit typed goes to the puzzle, not the dropdown.
          e.currentTarget.blur();
        }}
        className="h-9 min-w-16 appearance-none rounded-btn border border-line bg-surface pr-7 pl-3 font-sans text-[16px] font-bold text-ink tabular-nums"
      >
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <svg
        aria-hidden="true"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="pointer-events-none absolute top-1/2 right-[9px] -translate-y-1/2 text-muted"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </span>
  );
}

function Note({ title, sub }: { title: string; sub: string }) {
  return (
    <div
      data-helper-note
      className="flex min-h-[84px] flex-col items-center justify-center gap-[3px] rounded-btn bg-bg px-4 py-3 text-center"
    >
      <b className="text-[15px] font-bold text-balance text-ink">{title}</b>
      <span className="text-[13px] text-balance text-muted">{sub}</span>
    </div>
  );
}

const cellsWord = (n: number) => (n === 1 ? 'cell' : 'cells');

const HIDE_RED = 'zinc.sumHideRed';

function Switch(props: { label: string; on: boolean; onChange: () => void }) {
  return (
    <Field label={props.label}>
      <button
        type="button"
        role="switch"
        aria-checked={props.on}
        aria-label={props.label}
        onClick={props.onChange}
        className={`my-[5px] box-border flex h-[26px] w-11 rounded-[13px] border-0 p-[3px] ${
          props.on ? 'justify-end bg-accent' : 'justify-start bg-toggle-off'
        }`}
      >
        <span className="h-5 w-5 rounded-[10px] bg-surface shadow-[0_1px_2px_rgba(31,30,28,0.2)]" />
      </button>
    </Field>
  );
}

export function HelperPanel(props: {
  panelRef: RefObject<HTMLDivElement | null>;
  sum: SumValues;
  /** The arrow's circle can be "?" (not filled in), and what it's called in the messages. */
  circle: 'circle' | 'ellipse' | null;
  totals: number[];
  cellCounts: number[];
  view: SumView;
  digits: readonly string[];
  placed: readonly string[];
  digitMarks: Record<string, HelperMark>;
  at: 'top' | 'bottom';
  onSum: (next: SumValues) => void;
  onRow: (key: string) => void;
  onDigit: (digit: string) => void;
  onClose: () => void;
}) {
  const { sum, view, panelRef } = props;
  const [hideRed, setHideRed] = useState(() => {
    try {
      return localStorage.getItem(HIDE_RED) === '1';
    } catch {
      return false;
    }
  });
  const toggleHideRed = () =>
    setHideRed((on) => {
      try {
        localStorage.setItem(HIDE_RED, on ? '0' : '1');
      } catch {
        // Storage off: the switch still works until the page closes.
      }
      return !on;
    });
  // The panel's inner width, for how many tiles go across.
  const [width, setWidth] = useState(() => Math.min(window.innerWidth - 16, 420) - 24);
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth - 24);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [panelRef]);

  let body: ReactNode;
  if (view.kind === 'pick') {
    const what = props.circle ?? 'circle';
    body = (
      <Note
        title="Pick the total"
        sub={
          view.can.length
            ? `This ${what} can be ${listText(view.can)}.`
            : `No total fits this ${what} with ${sum.cells} ${cellsWord(sum.cells)}.`
        }
      />
    );
  } else if (view.kind === 'none') {
    const sub = !view.reach
      ? `There are only ${props.digits.length} different digits.`
      : sum.cells === 1
        ? `One cell holds ${view.reach[0]} to ${view.reach[1]}.`
        : `${sum.cells} ${sum.repeats ? '' : 'different '}digits add up to ${view.reach[0]} to ${view.reach[1]}.`;
    body = (
      <Note
        title={`No way to make ${sum.total} with ${sum.cells} ${cellsWord(sum.cells)}`}
        sub={sub}
      />
    );
  } else if (view.kind === 'ruled') {
    body = (
      <Note
        title="Nothing left"
        sub={
          view.byPlaced
            ? 'The digits placed in it fit no combination.'
            : 'Your marks rule out every combination.'
        }
      />
    );
  } else if (view.kind === 'over') {
    body = (
      <Note
        title="Too many combinations to list"
        sub="Mark digits in or out to narrow them down."
      />
    );
  } else if (hideRed && view.rows.every((r) => r.mark === 2)) {
    body = <Note title="Every combination is marked red" sub="Turn off Hide red to see them." />;
  } else {
    const rows = hideRed ? view.rows.filter((r) => r.mark !== 2) : view.rows;
    const chars = sum.cells * Math.max(...props.digits.map((d) => d.length));
    const { cols, scroll } = tileLayout(width, chars, rows.length);
    body = (
      <div
        data-helper-rows
        data-scroll={scroll ? 'true' : undefined}
        className={`grid content-start gap-1.5 ${scroll ? 'max-h-[228px] overflow-y-auto overscroll-contain pr-0.5' : ''}`}
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {rows.map((r) => (
          <button
            key={r.key}
            type="button"
            data-helper-row={r.key}
            data-mark={r.mark ?? ''}
            aria-pressed={r.mark === 1}
            aria-label={`${r.digits.join(' ')}${
              r.mark === 1 ? ': this one' : r.mark === 2 ? ': not this one' : ''
            }`}
            onClick={() => props.onRow(r.key)}
            className={`relative flex min-h-9 min-w-0 items-center justify-center gap-[3px] rounded-btn border px-2 text-[15px] font-semibold tabular-nums ${tileClass(r.mark)}`}
          >
            {r.mark === 2 && (
              <span
                aria-hidden="true"
                className="absolute inset-x-1.5 top-1/2 border-t-[1.5px] border-mistake"
              />
            )}
            {r.digits.map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </button>
        ))}
      </div>
    );
  }

  const always = view.kind === 'rows' ? view.always : new Set<string>();
  const never = view.kind === 'rows' ? view.never : new Set<string>();
  const digitCols =
    props.digits.length <= 9 ? props.digits.length : Math.ceil(props.digits.length / 2);
  // A clue's total is always in the list, even one past the usual range.
  const totals =
    sum.total !== null && !props.totals.includes(sum.total)
      ? [...props.totals, sum.total].sort((a, b) => a - b)
      : props.totals;
  const totalOptions = [
    ...(props.circle ? [{ value: '?', label: '?' }] : []),
    ...totals.map((t) => ({ value: String(t), label: String(t) })),
  ];
  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Sum tool"
      data-keeps-selection
      data-helper-at={props.at}
      className={`fixed inset-x-2 z-40 flex flex-col gap-2.5 rounded-[18px] border border-line bg-surface p-3 shadow-[0_8px_24px_rgba(31,30,28,0.16)] lg:right-6 lg:left-auto lg:w-[420px] ${
        props.at === 'top'
          ? 'top-[max(10px,env(safe-area-inset-top))]'
          : 'bottom-[max(10px,env(safe-area-inset-bottom))]'
      }`}
    >
      <div className="flex items-end gap-2.5">
        <Field label="Total">
          <Dropdown
            label="Total"
            value={sum.total === null ? '?' : String(sum.total)}
            options={totalOptions}
            onChange={(v) => props.onSum({ ...sum, total: v === '?' ? null : Number(v) })}
          />
        </Field>
        <Field label="Cells">
          <Dropdown
            label="Cells"
            value={String(sum.cells)}
            options={props.cellCounts.map((n) => ({ value: String(n), label: String(n) }))}
            onChange={(v) => props.onSum({ ...sum, cells: Number(v) })}
          />
        </Field>
        <Switch
          label="Repeats"
          on={sum.repeats}
          onChange={() => props.onSum({ ...sum, repeats: !sum.repeats })}
        />
        <Switch label="Hide red" on={hideRed} onChange={toggleHideRed} />
        <button
          type="button"
          onClick={props.onClose}
          className="ml-auto h-9 flex-none rounded-btn border border-line bg-surface px-3 text-[14px] font-semibold text-ink"
        >
          Done
        </button>
      </div>
      {body}
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${digitCols}, minmax(0, 1fr))` }}
      >
        {props.digits.map((d) => {
          const placed = props.placed.includes(d);
          const mark = props.digitMarks[d];
          const faded = !placed && !mark && never.has(d);
          return (
            <button
              key={d}
              type="button"
              data-helper-digit={d}
              data-mark={placed ? 'placed' : (mark ?? '')}
              aria-pressed={placed || mark === 1}
              aria-disabled={placed}
              aria-label={`${d}${placed ? ': placed' : mark === 1 ? ': in' : mark === 2 ? ': not in' : ''}`}
              onClick={() => {
                if (!placed) props.onDigit(d);
              }}
              className={`relative rounded-btn border font-medium ${
                props.digits.length > 9
                  ? 'h-8 text-[14px]'
                  : props.digits.length > 6
                    ? 'h-9 text-[16px]'
                    : 'h-10 text-[18px]'
              } ${placed ? 'border-solved bg-solved font-bold text-white' : digitClass(mark)} ${
                faded ? 'opacity-35' : ''
              }`}
            >
              {d}
              {always.has(d) && (
                <span
                  aria-hidden="true"
                  className={`absolute bottom-1 left-1/2 h-[5px] w-[5px] -translate-x-1/2 rounded-full ${
                    placed ? 'bg-white' : 'bg-solved'
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
